"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Checkbox,
	Form,
	FormField,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@layer/ui/components";
import { StandardFormItem } from "@layer/ui/patterns";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { MerchantStatus } from "@/api/types/merchants.types";
import {
	CreateMerchantSchema,
	type CreateMerchantSchemaType,
	createMerchantDefaultValues,
} from "./create-merchant-form.schema";

type CreateMerchantFormProps = {
	onClose: () => void;
};

const CreateMerchantForm = ({ onClose }: CreateMerchantFormProps) => {
	const t = useTranslations("Features.merchants.createMerchant");
	const tFields = useTranslations("Features.merchants.createMerchant.fields");

	const form = useForm<z.infer<CreateMerchantSchemaType>>({
		resolver: zodResolver(CreateMerchantSchema(tFields)),
		defaultValues: createMerchantDefaultValues,
		mode: "onChange",
	});

	const onSubmit = (_data: z.infer<CreateMerchantSchemaType>) => {
		onClose();
	};

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="flex flex-col h-full"
			>
				<div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
					<FormField
						control={form.control}
						name="merchantName"
						render={({ field }) => (
							<StandardFormItem
								label={tFields("merchantName.label")}
								gridColSpan={2}
							>
								<Input
									{...field}
									placeholder={tFields(
										"merchantName.placeholder",
									)}
								/>
							</StandardFormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="merchantLogo"
						render={({
							field: { onChange, value: _value, ...rest },
						}) => (
							<StandardFormItem
								label={tFields("merchantLogo.label")}
								gridColSpan={2}
							>
								<input
									type="file"
									accept="image/*"
									onChange={(e) =>
										onChange(e.target.files?.[0] ?? null)
									}
									className="file:mr-3 file:rounded-sm file:border-0 file:bg-app-bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-app-text cursor-pointer w-full rounded-md border border-input-border bg-input-bg/30 px-3 py-1.5 text-sm text-app-text outline-none transition-colors"
									{...rest}
								/>
							</StandardFormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="merchantId"
						render={({ field }) => (
							<StandardFormItem
								label={tFields("merchantId.label")}
								gridColSpan={2}
							>
								<Input
									{...field}
									placeholder={tFields(
										"merchantId.placeholder",
									)}
								/>
							</StandardFormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="status"
						render={({ field }) => (
							<StandardFormItem
								label={tFields("status.label")}
								gridColSpan={2}
							>
								<Select
									value={field.value}
									onValueChange={field.onChange}
								>
									<SelectTrigger>
										<SelectValue
											placeholder={tFields(
												"status.placeholder",
											)}
										/>
									</SelectTrigger>
									<SelectContent>
										{Object.values(MerchantStatus).map(
											(status) => (
												<SelectItem
													key={status}
													value={status}
												>
													{tFields(
														`status.options.${status}`,
													)}
												</SelectItem>
											),
										)}
									</SelectContent>
								</Select>
							</StandardFormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="addToAllowedList"
						render={({ field }) => (
							<div className="flex items-start gap-3 rounded-lg border border-app-border p-4">
								<Checkbox
									id="addToAllowedList"
									checked={field.value}
									onCheckedChange={field.onChange}
									className="mt-0.5"
								/>
								<div className="flex flex-col gap-1">
									<Label
										htmlFor="addToAllowedList"
										className="text-sm font-medium cursor-pointer"
									>
										{tFields("addToAllowedList.title")}
									</Label>
									{!field.value && (
										<p className="text-xs text-app-text-muted">
											{tFields(
												"addToAllowedList.subtitle",
											)}
										</p>
									)}
								</div>
							</div>
						)}
					/>
				</div>

				<div className="border-t px-4 py-3 flex items-center justify-end gap-3 shrink-0">
					<Button variant="outline" type="button" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button type="submit">{t("saveAndContinue")}</Button>
				</div>
			</form>
		</Form>
	);
};

export { CreateMerchantForm };
